import React, { useState, useEffect, useMemo } from 'react'
import { Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import {
	onlyNumbers,
	LINE_ITEM_TYPES,
	PROMO_API_ACTIONS,
	PROMO_SERVICE_TARGETS,
	getVisitPromoServiceTargets,
	resolveFacilityVisitId,
	resolveHouseholdId
} from '../../../utils'
import StyledSelect from '../../Common/StyledSelect'
import StyledCurrencyTextField from '../../Common/StyledCurrencyTextField'
import StyledTextField from '../../Common/StyledTextField'
import StyledAlert from '../../Common/StyledAlert'
import usePromotions from '../../../hooks/usePromotions'

const useStyles = makeStyles(() => ({
	dialogActions: {
		justifyContent: 'flex-start'
	},
	dialogContent: {
		display: 'flex',
		flexDirection: 'column',
		gap: '1rem',
		margin: '.5rem 0 24px 0'
	},
	promoCodeEntryContent: {
		display: 'flex',
		flexDirection: 'column',
		gap: '.5rem'
	},
	fieldWrapper: {
		'& .MuiTextField-root': {
			minWidth: '100%'
		},
		'& .MuiFilledInput-underline': {
			minWidth: '100%'
		}
	}
}))

const PROMO_ENTRY_TYPES = {
	Promo_Code: 'promocode',
	Manual_Discount: 'manual'
}

const capitalizeFirstLetter = (string) => {
	return string.charAt(0).toUpperCase() + string.slice(1)
}

const PromoAndDiscountEntryDialog = ({
	open,
	apiAction = PROMO_API_ACTIONS.Redeem,
	price,
	visit,
	household_id,
	market_id,
	facility_id,
	service_target,
	onCancel,
	onComplete
}) => {
	const classes = useStyles()

	const { redeemPromoForFacilityVisit, validatePromoForServiceTarget, validatePromoForFacilityVisit, promoLoading } =
		usePromotions()

	const [subtext, setSubtext] = useState('')
	const [amount, setAmount] = useState(0)
	const [promoCode, setPromoCode] = useState('')
	// const [promoEntryType, setPromoEntryType] = useState(PROMO_ENTRY_TYPES.Promo_Code)
	const [promoEntryType, setPromoEntryType] = useState(PROMO_ENTRY_TYPES.Manual_Discount)
	const [hasPromo, setHasPromo] = useState(false)
	const [existingPromo, setExistingPromo] = useState(null)
	const [promoError, setPromoError] = useState('')

	const price_number = Number(onlyNumbers(amount))

	useEffect(() => {
		if (visit && open) {
			const visitPromo = visit?.lineItems?.find((i) => i.type === LINE_ITEM_TYPES.VisitPromo)
			const appliedPromo = visit?.applied_promo || null

			if (visitPromo) {
				setExistingPromo(visitPromo)
				setPromoEntryType(PROMO_ENTRY_TYPES.Manual_Discount)
				setHasPromo(true)
				setSubtext(visitPromo.subtext || '')
				setAmount(visitPromo.amount * -1)
				return
			}
			if (appliedPromo) {
				setExistingPromo(appliedPromo)
				setPromoEntryType(PROMO_ENTRY_TYPES.Promo_Code)
				setHasPromo(true)
				setPromoCode(appliedPromo.code || '')
				return
			}

			setExistingPromo(null)
		}
	}, [visit, open])

	const visitServiceTargets = useMemo(() => {
		if (!visit) {
			return []
		}

		let daycareCreditsApplied = false
		if (visit.type === 'daycare' && visit.household?.credits?.daycare?.length) {
			const availableCredits = visit.household.credits.daycare
				.filter((credit) => !credit.expired && !credit.used && credit.credit_remaining > 0)
				.reduce((acc, credit) => acc + credit.credit_remaining, 0)
			daycareCreditsApplied = availableCredits > 0
		}

		return getVisitPromoServiceTargets(visit, { daycareCreditsApplied })
	}, [visit])

	const handleApplyPromoCodeForFacilityVisit = async () => {
		const visitId = resolveFacilityVisitId(visit)
		const householdId = resolveHouseholdId(visit?.household_id || household_id)

		if (!visitId) {
			setPromoError('Missing visit id')
			return
		}

		if (!householdId) {
			setPromoError('Missing household id')
			return
		}

		const serviceTargets = visitServiceTargets.length > 0 ? visitServiceTargets : [visit.type]
		const errors = []
		let bestPromo = null
		let bestServiceTarget = null

		for (const serviceTarget of serviceTargets) {
			const { valid, applicable_promo, validation_error } = await validatePromoForFacilityVisit({
				promo_code: promoCode,
				household_id: householdId,
				visit_id: visitId,
				service_target: serviceTarget
			})

			if (valid && applicable_promo) {
				bestPromo = applicable_promo
				bestServiceTarget = serviceTarget
			}
			if (validation_error) {
				errors.push(validation_error)
			}
		}

		if (bestPromo && bestServiceTarget) {
			const { success, redemption_error } = await redeemPromoForFacilityVisit({
				promo_code: promoCode,
				visit_id: visitId,
				household_id: householdId,
				service_target: bestServiceTarget
			})

			if (success) {
				handleOnComplete({ redeemed_promo: promoCode })
			} else if (redemption_error) {
				setPromoError(redemption_error)
			}
			return
		}

		if (errors.length > 0) {
			setPromoError(errors.length > 1 ? 'This code is not applicable.' : errors[0])
		}
	}

	const handleApplyPromoCode = async () => {
		try {
			if (apiAction === PROMO_API_ACTIONS.Redeem) {
				await handleApplyPromoCodeForFacilityVisit()
			}
			if (apiAction === PROMO_API_ACTIONS.Validate) {
				const { applicable_promo, valid, validation_error } = await validatePromoForServiceTarget({
					promo_code: promoCode,
					household_id: household_id,
					market_id: market_id,
					facility_id: facility_id,
					service_target: service_target
				})
				if (valid) {
					handleOnComplete({ validated_promo: applicable_promo })
				}
				if (validation_error) {
					setPromoError(validation_error)
				}
			}
		} catch (e) {
			console.log(e)
		}
	}

	const handleOnCancel = () => {
		if (onCancel) {
			onCancel()
		}
	}

	const handleOnComplete = ({ redeemed_promo, validated_promo, manual_discount }) => {
		if (onComplete) {
			onComplete({ redeemed_promo, validated_promo, manual_discount })
		}
	}

	const handlePromoTypeEntryChange = (e) => {
		const { value } = e.target
		setPromoEntryType(value)
		if (value === PROMO_ENTRY_TYPES.Manual_Discount) {
			setPromoCode('')
			setPromoError('')
		}
		if (value === PROMO_ENTRY_TYPES.Promo_Code) {
			setAmount(0)
			setSubtext('')
		}
	}

	const handleApplyManualDiscount = () => {
		if (promoEntryType === PROMO_ENTRY_TYPES.Manual_Discount) {
			let obj = {}
			if (visit) {
				obj = {
					visit_id: visit?._id || '',
					household_id: visit?.household_id || household_id,
					type: 'visit_promo',
					amount: price_number * -1,
					description: capitalizeFirstLetter(`${visit.type.replace('_', ' ')} Appointment Promo`),
					subtext: subtext,
					subtype: 'manual_discount'
				}
			}
			if (household_id && service_target === PROMO_SERVICE_TARGETS.BAR_PARK) {
				obj = {
					household_id: household_id,
					type: 'manual_discount',
					amount: price_number * -1,
					description:
						subtext > ''
							? subtext
							: capitalizeFirstLetter(`${service_target.replace('_', ' ')} Discount`),
					subtype: 'manual_discount'
				}
			}

			handleOnComplete({ manual_discount: obj })
		}
	}

	const disableSave = () => {
		if (promoLoading) {
			return true
		}
		if (promoEntryType === PROMO_ENTRY_TYPES.Manual_Discount) {
			return !subtext || !amount || price_number < 0 || price_number > price
		}
		if (promoEntryType === PROMO_ENTRY_TYPES.Promo_Code) {
			return !promoCode || promoCode.length < 5
		}
		return true
	}

	return (
		<Dialog
			open={open}
			className={classes.dialog}
			onClose={() => {
				handleOnCancel()
			}}
			TransitionProps={{
				onExit: () => {
					console.log('onExit')
					setSubtext('')
					setAmount(0)
					setPromoCode('')
					setPromoError('')
				}
			}}
			PaperProps={{
				style: {
					minWidth: '448px',
					maxWidth: '448px',
					width: '448px'
				}
			}}>
			<DialogTitle
				style={{
					marginBottom: 24,
					textTransform: 'uppercase'
				}}>
				{existingPromo
					? promoEntryType === PROMO_ENTRY_TYPES.Promo_Code
						? 'Edit Promo'
						: 'Edit Discount'
					: 'Add Promo or Discount'}
			</DialogTitle>
			<DialogContent>
				<div className={classes.dialogContent}>
					<StyledSelect
						label="Type"
						required={true}
						value={promoEntryType}
						disabled={existingPromo}
						onChange={handlePromoTypeEntryChange}>
						{Object.entries(PROMO_ENTRY_TYPES).map(([key, value]) => {
							return (
								<MenuItem key={key} value={value}>
									{key.split('_').join(' ')}
								</MenuItem>
							)
						})}
					</StyledSelect>
					{promoEntryType === PROMO_ENTRY_TYPES.Manual_Discount ? (
						<>
							<div className={classes.fieldWrapper}>
								<TextField
									label="Discount Name"
									variant="filled"
									value={subtext || ''}
									onChange={(e) => setSubtext(e.target.value)}
								/>
							</div>
							<div className={classes.fieldWrapper} style={{ marginBottom: 0 }}>
								<StyledCurrencyTextField
									label="Discount Amount"
									variant="filled"
									initialValue={amount}
									isSubtraction={true}
									amountChanged={(e) => setAmount(e.inCents)}
								/>
							</div>
						</>
					) : null}
					{promoEntryType === PROMO_ENTRY_TYPES.Promo_Code ? (
						<div className={classes.promoCodeEntryContent}>
							<StyledTextField
								variant="filled"
								label="Promo Code"
								value={promoCode}
								required={true}
								inputProps={{ maxLength: 15 }}
								onChange={(e) => setPromoCode(e.target.value)}
							/>
							{promoError > '' ? <StyledAlert type="error" text={promoError} /> : null}
						</div>
					) : null}
				</div>
			</DialogContent>
			<DialogActions className={classes.dialogActions}>
				<Button
					variant="contained"
					onClick={() => {
						if (promoEntryType === PROMO_ENTRY_TYPES.Promo_Code) {
							handleApplyPromoCode()
						} else {
							handleApplyManualDiscount()
						}
					}}
					color="secondary"
					disabled={disableSave()}>
					Apply
				</Button>
				<Button variant="outlined" onClick={handleOnCancel} color="secondary">
					CANCEL
				</Button>
			</DialogActions>
		</Dialog>
	)
}

export default PromoAndDiscountEntryDialog
